/**
 * Global feature-shortcut RULES — pure, DOM-free, unit-testable.
 *
 * The React wiring lives in `useGlobalHotkeys.ts`; everything here is a plain
 * function so vitest (which runs in a `node` environment, no jsdom) can cover
 * the three guards and the key -> action mapping directly.
 *
 * Guards (Gmail/GitHub style — all three are mandatory):
 *   (a) never fire while focus is in an input/textarea/select/contenteditable
 *   (b) never fire while a modal/lightbox is open (Escape stays the only key)
 *   (c) never fire while a modifier (meta/ctrl/alt) is held, so Cmd+R / Ctrl+R
 *       browser reload — and every other browser shortcut — is untouched.
 */

/** One entry per Feature Selector item. */
export type FeatureAction =
  | 'resume-editor'
  | 'job-manager'
  | 'activity-dashboard'
  | 'writing-sample'
  | 'application-qa'
  | 'interview-qa'
  | 'help';

/** key (as typed) -> feature. `h` and `?` both open Help & Tips. */
export const HOTKEYS: Readonly<Record<string, FeatureAction>> = {
  r: 'resume-editor',
  j: 'job-manager',
  d: 'activity-dashboard',
  w: 'writing-sample',
  a: 'application-qa',
  i: 'interview-qa',
  h: 'help',
  '?': 'help',
};

/** The <kbd> chip rendered next to each Feature Selector menu item. */
export const HOTKEY_CHIP: Readonly<Record<FeatureAction, string>> = {
  'resume-editor': 'r',
  'job-manager': 'j',
  'activity-dashboard': 'd',
  'writing-sample': 'w',
  'application-qa': 'a',
  'interview-qa': 'i',
  help: 'h',
};

/**
 * In-tab routes for the navigational features. Deliberately different from the
 * Feature Selector menu items, which `window.open()` a NEW tab — a keystroke
 * that silently spawns tabs is hostile, so hotkeys navigate in place.
 *
 * NOTE: `/activity-report` is kept as the canonical path even though the label
 * is now "Activity Dashboard" — bookmarks, help targets and UAT specs use it.
 */
export const FEATURE_ROUTES: Readonly<Record<string, string>> = {
  'job-manager': '/',
  'activity-dashboard': '/activity-report',
  'application-qa': '/application-qa',
  'interview-qa': '/interview-qa',
};

/** Features that are Dashboard-owned MODALS rather than routes. */
export const MODAL_FEATURES: readonly FeatureAction[] = ['resume-editor', 'writing-sample'];

/** Router-state key used to ask the Dashboard to open a modal feature on mount. */
export const OPEN_FEATURE_STATE_KEY = 'openFeature';

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION']);
const EDITABLE_ROLES = new Set(['textbox', 'searchbox', 'combobox', 'spinbutton']);

/**
 * Anything that renders as an open modal/lightbox. Every SkillSnap lightbox
 * carries `role="dialog"` + `aria-modal="true"` and is CONDITIONALLY rendered
 * (`if (!isOpen) return null`), so presence in the DOM means "open".
 * `[data-hotkey-block]` is an explicit opt-out for any future overlay that
 * cannot use the dialog role.
 */
export const MODAL_SELECTOR =
  '[role="dialog"],[role="alertdialog"],[aria-modal="true"],[data-hotkey-block]';

/** Minimal element shape we probe — keeps this file DOM-typing-free. */
interface ElementLike {
  tagName?: string;
  isContentEditable?: boolean;
  getAttribute?(name: string): string | null;
  closest?(selector: string): unknown;
}

interface DocumentLike {
  querySelectorAll(selector: string): ArrayLike<ElementLike>;
}

/** Guard (a): is the event target something the user is typing into? */
export function isEditableTarget(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false;
  const el = target as ElementLike;

  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  if (EDITABLE_TAGS.has(tag)) return true;
  if (el.isContentEditable === true) return true;

  if (typeof el.getAttribute === 'function') {
    const ce = el.getAttribute('contenteditable');
    if (ce !== null && ce !== undefined && ce !== 'false') return true;
    const role = el.getAttribute('role');
    if (role && EDITABLE_ROLES.has(role)) return true;
  }

  // A caret inside a nested rich-text region: the target may be a child node.
  if (typeof el.closest === 'function') {
    try {
      if (el.closest('[contenteditable=""],[contenteditable="true"]')) return true;
    } catch {
      /* document/window targets have no closest() — not editable */
    }
  }
  return false;
}

/** Guard (b), DOM half: is any modal/lightbox element currently rendered? */
export function hasOpenModalElement(doc: DocumentLike | null | undefined): boolean {
  if (!doc || typeof doc.querySelectorAll !== 'function') return false;
  const nodes = doc.querySelectorAll(MODAL_SELECTOR);
  for (let i = 0; i < nodes.length; i += 1) {
    const el = nodes[i];
    const hidden = typeof el?.getAttribute === 'function' ? el.getAttribute('aria-hidden') : null;
    if (hidden === 'true') continue; // rendered-but-hidden drawer, not a modal
    return true;
  }
  return false;
}

/** Hotkeys are bound on every authenticated full page EXCEPT the admin panel. */
export function isHotkeyDisabledPath(pathname: string | undefined | null): boolean {
  return typeof pathname === 'string' && pathname.startsWith('/admin');
}

/** The subset of a KeyboardEvent the resolver reads. */
export interface HotkeyEventLike {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  defaultPrevented?: boolean;
  target?: unknown;
}

/**
 * Apply the three guards and map the key to a feature.
 * Returns null when the keystroke must be left alone.
 */
export function resolveHotkey(
  event: HotkeyEventLike | null | undefined,
  ctx: { modalOpen: boolean },
): FeatureAction | null {
  if (!event || typeof event.key !== 'string') return null;
  if (event.defaultPrevented) return null;

  // (c) modifiers — Cmd+R / Ctrl+R / Alt+… stay with the browser.
  if (event.metaKey || event.ctrlKey || event.altKey) return null;

  // (b) a modal owns the keyboard; Escape (handled by useEscapeClose) is it.
  if (ctx.modalOpen) return null;

  // (a) the user is typing.
  if (isEditableTarget(event.target)) return null;

  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  const action = HOTKEYS[key];
  if (!action) return null;

  // Shift is only legitimate for `?` (which requires it on most layouts);
  // Shift+R etc. is almost always the start of typing somewhere untracked.
  if (event.shiftKey && key !== '?') return null;

  return action;
}
